import { isRecord, type Doc } from '@day';
import { couriersOf, deadlineOn } from './waiting';

/**
 * Stops changed in one go (Change… on a selection, actions.ts `changeStops` and `changeWaiting`): what a stop has today,
 * worked out here with nothing written, so it's tested on its own (tests/batch.test.ts). Proof and arrival windows aren't
 * in the records yet, so they aren't here either.
 */

/** What changes; what isn't given stays as it is. */
export interface StopChange {
  type?: 'delivery' | 'pickup';
  /** Minutes at the stop (the phone's 1 to 60); null: the courier's usual. */
  dwell?: number | null;
  /** A deliver-by time ("14:30"): today's on a route waiting to start, on the day it goes out for a waiting stop. Null: none. */
  deliverBy?: string | null;
  /** Waiting stops only: the only couriers allowed to take them; null: anyone. */
  couriers?: string[] | null;
}

const kind = (s: Doc) => (s.type === 'pickup' ? 'pickup' : 'delivery');

/**
 * A route's stored stops with those in [ids] changed (the order stays). A pickup that a delivery on the route names stays a
 * pickup, or the delivery would lose where it's collected ([fed] counts them); a delivery that becomes a pickup stops naming
 * one. [n]: how many stops changed.
 */
export function changedOnRoute(stops: Doc[], ids: Set<string>, c: StopChange, today: string): { stops: Doc[]; n: number; fed: number } {
  const feeds = new Set(stops.map((s) => s.pickupId).filter((x): x is string => typeof x === 'string'));
  let n = 0;
  let fed = 0;
  const out = stops.map((s) => {
    if (!ids.has(String(s.id))) return s;
    const next: Doc = { ...s };
    if (c.type && kind(s) !== c.type) {
      if (c.type === 'delivery' && feeds.has(String(s.id))) fed++;
      else {
        next.type = c.type;
        if (c.type === 'pickup' && s.pickupId != null) next.pickupId = null;
      }
    }
    if (c.dwell !== undefined) next.dwellMinutes = c.dwell;
    // Stored as a sent route's deadline is (a phone's local time, no zone): the route goes out today.
    if (c.deliverBy !== undefined) next.deliverBy = c.deliverBy ? deadlineOn(today, c.deliverBy) : null;
    const same = Object.keys(next).every((k) => (next[k] ?? null) === (s[k] ?? null));
    if (!same) n++;
    return same ? s : next;
  });
  return { stops: out, n, fed };
}

/**
 * A waiting stop's record with [c] made: the record as it'll read ([next]) and the fields to write ([fields]: a stored
 * stop's as `stop.type`, null taking `byTime` or `couriers` off). Null when nothing changes; [fed] when it stayed a pickup
 * because a waiting delivery names it.
 */
export function changedWaiting(raw: Doc, c: StopChange, feeds: boolean): { next: Doc; fields: Doc } | { fed: true } | null {
  if (!isRecord(raw.stop)) return null;
  const was = raw.stop as Doc;
  const stop: Doc = { ...was };
  const next: Doc = { ...raw, stop };
  const fields: Doc = {};
  let fed = false;
  const put = (k: string, v: unknown) => {
    fields[`stop.${k}`] = v;
    stop[k] = v;
  };
  if (c.type && kind(was) !== c.type) {
    if (c.type === 'delivery' && feeds) fed = true;
    else {
      put('type', c.type);
      if (c.type === 'pickup' && was.pickupId != null) put('pickupId', null);
    }
  }
  if (c.dwell !== undefined && (was.dwellMinutes ?? null) !== c.dwell) put('dwellMinutes', c.dwell);
  if (c.deliverBy !== undefined && (typeof raw.byTime === 'string' ? raw.byTime : null) !== c.deliverBy) {
    fields.byTime = c.deliverBy;
    if (c.deliverBy) next.byTime = c.deliverBy;
    else delete next.byTime;
    // A deadline on a day of its own gives way to the time set here.
    if (was.deliverBy != null) put('deliverBy', null);
  }
  if (c.couriers !== undefined) {
    const to = couriersOf(c.couriers);
    if ((to ?? []).join() !== (couriersOf(raw.couriers) ?? []).join()) {
      fields.couriers = to;
      if (to) next.couriers = to;
      else delete next.couriers;
    }
  }
  if (Object.keys(fields).length) return { next, fields };
  return fed ? { fed: true } : null;
}
